import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RotateCcw } from 'lucide-react';
import GroupLogoUploader from '../GroupLogoUploader';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import Avatar from '../ui/avatar-initials';
import { firstName } from '../../lib/play';

const FIELDS = [
  { section: 'Rating engine', items: [
    { key: 'start_rating', label: 'Starting rating', hint: 'Everyone begins here' },
    { key: 'k_new', label: 'K (placing)', hint: 'How fast new players move' },
    { key: 'k_established', label: 'K (established)', hint: 'How fast veterans move' },
    { key: 'placement_matches', label: 'Placement matches', hint: 'Games before ranked' },
    { key: 'margin_bonus_max', label: 'Margin bonus (max)', hint: '0.2 = up to +20% for blowouts', step: '0.05' },
  ] },
  { section: 'Court mode', items: [
    { key: 'points_to_win', label: 'Points to win', hint: 'Default score in the logger' },
    { key: 'match_minutes', label: 'Minutes per match', hint: 'Rounds = session length ÷ this' },
    { key: 'avoid_repeat_partner_sessions', label: 'Avoid repeat partners (sessions)', hint: '0 = off' },
    { key: 'repeat_partner_penalty', label: 'Repeat-partner weight', hint: 'Rating pts; balance always comes first' },
    { key: 'edit_window_hours', label: 'Scorer edit window (hours)', hint: 'Admins can always edit' },
  ] },
  { section: 'Fun stuff', items: [
    { key: 'giant_slayer_gap', label: 'Giant Slayer gap', hint: 'Rating gap for an upset' },
    { key: 'giant_slayer_bonus', label: 'Giant Slayer bonus', hint: 'Extra points per winner' },
    { key: 'streak_alert_at', label: 'On-fire streak', hint: 'Wins in a row to announce' },
    { key: 'season_reset_factor', label: 'Season soft reset', hint: '0.5 = keep half the gap from start', step: '0.1' },
  ] },
];

export default function PlaySettingsSheet({ open, onClose, group, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [scorers, setScorers] = useState([]);
  const [saving, setSaving] = useState(false);
  const members = group?.members || [];
  const adminIds = group?.admin_ids || [];

  useEffect(() => {
    if (!open || !group?.id) return;
    api.get(`/groups/${group.id}/play/settings`).then((r) => {
      setCfg(r.data.settings);
      setScorers(r.data.scorer_ids || []);
    }).catch(() => toast.error('Could not load settings'));
  }, [open, group?.id]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const toggleScorer = (id) => setScorers((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    setSaving(true);
    try {
      const settings = {};
      Object.entries(cfg).forEach(([k, v]) => {
        if (k === 'enabled' || k === 'notify_whatsapp' || k === 'auto_send_schedule' || k === 'share_format') settings[k] = v;
        else settings[k] = Number(v);
      });
      await api.put(`/groups/${group.id}/play/settings`, { settings, scorer_ids: scorers });
      toast.success('Point system updated');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const r = await api.get(`/groups/${group.id}/play/settings`);
    setCfg({ ...r.data.defaults, enabled: cfg.enabled, notify_whatsapp: cfg.notify_whatsapp, auto_send_schedule: cfg.auto_send_schedule, share_format: cfg.share_format });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[92vh] overflow-y-auto" data-testid="play-settings-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl">Point system settings</SheetTitle>
          <SheetDescription>Everything is tweakable. Changing rating numbers recomputes every match instantly.</SheetDescription>
        </SheetHeader>
        {!cfg ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-8" /> : (
          <div className="space-y-5 mt-4 pb-4">
            <Row label="Play tab enabled" hint="Auto-on for racket sports">
              <Switch checked={cfg.enabled ?? true} onCheckedChange={(v) => set('enabled', v)} data-testid="play-enabled-switch" />
            </Row>
            <Row label="WhatsApp shout-outs" hint="Giant Slayer / crown steal / streaks">
              <Switch checked={!!cfg.notify_whatsapp} onCheckedChange={(v) => set('notify_whatsapp', v)} data-testid="play-whatsapp-switch" />
            </Row>
            <Row label="Auto-send schedule" hint="WhatsApp the court schedule to players on every shuffle">
              <Switch checked={cfg.auto_send_schedule !== false} onCheckedChange={(v) => set('auto_send_schedule', v)} data-testid="play-autosend-switch" />
            </Row>
            <div className="rounded-xl border p-3" data-testid="share-format-row">
              <p className="text-sm font-semibold">What to send on WhatsApp</p>
              <p className="text-xs text-muted-foreground mb-2">Branded PDF looks best and opens full-size; image shows inline in chat.</p>
              <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
                {[['pdf', 'PDF'], ['image', 'Image'], ['both', 'Both']].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => set('share_format', k)} data-testid={`share-format-${k}`}
                    className={`h-8 rounded-md text-xs font-bold transition-colors ${(cfg.share_format || 'pdf') === k ? 'bg-background shadow' : 'text-muted-foreground'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <GroupLogoUploader group={group} onChanged={onSaved} />

            <div>
              <p className="font-display text-xs font-bold uppercase tracking-[0.16em] mb-2">Scorers</p>
              <p className="text-xs text-muted-foreground mb-2">Admins can always log matches. Tap to add more scorers.</p>
              <div className="flex flex-wrap gap-2" data-testid="scorer-chips">
                {members.map((m) => {
                  const isAdmin = adminIds.includes(m.id);
                  const on = isAdmin || scorers.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      disabled={isAdmin}
                      onClick={() => toggleScorer(m.id)}
                      data-testid={`scorer-chip-${m.id}`}
                      className={`inline-flex items-center gap-1.5 pl-1 pr-3 h-8 rounded-full border text-xs font-semibold transition-colors ${on ? 'bg-amber-500/15 border-amber-500/50' : 'bg-background border-border text-muted-foreground'} ${isAdmin ? 'opacity-70' : ''}`}
                    >
                      <Avatar name={m.name} size="xs" /> {firstName(m.name)}{isAdmin ? ' · admin' : ''}
                    </button>
                  );
                })}
              </div>
            </div>

            {FIELDS.map((sec) => (
              <div key={sec.section}>
                <p className="font-display text-xs font-bold uppercase tracking-[0.16em] mb-2">{sec.section}</p>
                <div className="grid grid-cols-2 gap-2">
                  {sec.items.map((f) => (
                    <label key={f.key} className="rounded-xl border p-2.5 block">
                      <span className="text-xs font-semibold block">{f.label}</span>
                      <Input
                        type="number"
                        inputMode="decimal"
                        step={f.step || '1'}
                        value={cfg[f.key] ?? ''}
                        onChange={(e) => set(f.key, e.target.value)}
                        className="h-9 mt-1 tabular-nums"
                        data-testid={`play-setting-${f.key}`}
                      />
                      <span className="text-xs text-muted-foreground block mt-1">{f.hint}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full" onClick={reset} data-testid="play-settings-reset">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Defaults
              </Button>
              <Button className="rounded-full flex-1" onClick={save} disabled={saving} data-testid="play-settings-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border p-3">
      <div>
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      {children}
    </div>
  );
}
